// A Consolidation draft read by day, then by driver day sheet (« tournée »): 150 orders stay readable.
import { billingNumber } from "./billingCalculation.js";
import { groupBillableCalculationDays, summarizeBillableTime } from "./billingCalculationDays.js";

const unique = (values) => [...new Set(values.filter(Boolean))];
export const normalizedSearch = (value) => String(value ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const cents = (rows) => rows.reduce((total, { row }) => total + Math.round((billingNumber(row.line_total) ?? 0) * 100), 0);

/** Invoice lines read like billable items: loading, inspection and return carry a truck, not an order. */
export function invoiceLineItems(lines = []) {
  return lines.map((line) => line?.presentation_context?.order_id || !line?.order_reference ? line : { ...line, order_reference: null });
}

const rowDetails = (row) => [row?.calculation_details, ...(row?.calculation_details?.components || []).map((component) => component?.calculation_details)];
// A stop is read in the order of the day: its earliest saved block, rows without one last.
const rowStart = ({ row }) => rowDetails(row).map((value) => value?.block?.start).filter(Boolean).sort()[0] || "~";

/** Day sheet of a displayed row: its date and the Consolidation revision of its first component. */
export function invoiceRowTourKey(row) {
  const revision = rowDetails(row).map((value) => value?.revision_id).find(Boolean);
  // Un Chargé PM est daté du jour de son chargement, comme sa tournée.
  const date = rowDetails(row).map((value) => value?.execution_date).find(Boolean) || row.service_date;
  return revision && date ? `${date}:${revision}` : null;
}

function tourSummary(source, rows) {
  const deliveries = source.steps.filter((step) => step.kind === "delivery" || step.kind === "dompeur");
  const tons = deliveries.reduce((total, step) => total === null || step.tons === null ? null : total + step.tons, 0);
  const loads = unique(source.steps.map((step) => step.kind === "loading" ? step.site : step.kind === "preload" ? "Préchargement" : null));
  const clients = unique(deliveries.map((step) => step.site));
  const orders = unique(deliveries.flatMap((step) => step.orders.map((order) => order.order_reference)));
  const summary = { driver: source.driver, truck: source.truck, loads, clients, orders: orders.length, tons: deliveries.length ? tons : null,
    time: summarizeBillableTime(source.items), amount: cents(rows) / 100 };
  const text = [summary.driver, summary.truck, ...loads, ...clients,
    ...rows.flatMap(({ row }) => [row.order_reference, row.client_name, row.description])];
  return { ...summary, search: normalizedSearch(text.join(" ")) };
}

/**
 * Displayed rows (`groupInvoiceLinesForDisplay`) grouped by day and day sheet; the raw lines give
 * the driver, the sites and the road / on-site split. Rows without a day sheet (manual, historical)
 * stay in `other`, in their original order.
 */
export function groupInvoiceRows(rows = [], rawLines = []) {
  const sources = new Map(groupBillableCalculationDays(invoiceLineItems(rawLines).filter((line) => line?.calculation_details?.revision_id))
    .map((source, position) => [source.key, { source, position }]));
  const days = new Map();
  const other = [];
  rows.forEach((row, index) => {
    const key = invoiceRowTourKey(row);
    const found = key ? sources.get(key) : null;
    if (!found) { other.push({ row, index }); return; }
    const { date } = found.source;
    if (!days.has(date)) days.set(date, { key: date, date, tours: new Map() });
    const tours = days.get(date).tours;
    if (!tours.has(key)) tours.set(key, { key, position: found.position, source: found.source, rows: [] });
    tours.get(key).rows.push({ row, index });
  });
  const grouped = [...days.values()].sort((a, b) => a.date.localeCompare(b.date)).map((day) => {
    const tours = [...day.tours.values()].sort((a, b) => a.position - b.position).map(({ key, source, rows: tourRows }) => ({
      key, source, rows: tourRows.sort((a, b) => rowStart(a).localeCompare(rowStart(b)) || a.index - b.index), ...tourSummary(source, tourRows) }));
    const hours = tours.reduce((total, tour) => total === null || tour.time.hours == null ? null : total + tour.time.hours, 0);
    return { key: day.key, date: day.date, tours, hours, amount: tours.reduce((total, tour) => total + Math.round(tour.amount * 100), 0) / 100 };
  });
  return { days: grouped, other, tourCount: grouped.reduce((total, day) => total + day.tours.length, 0) };
}
